import React, { useEffect, useMemo, useState } from "react";
import { useAdminAuth } from "../AdminAuthContext";
import { PageHeader, Panel, Badge, Button } from "../components/UIKit";
import { Save, Search, Info } from "lucide-react";

const CATEGORIES = [
  { value: "all", label: "Tutte" },
  { value: "sleep", label: "Sonno" },
  { value: "physio", label: "Fisiologia" },
  { value: "audio", label: "Audio" },
  { value: "diary", label: "Diario" },
  { value: "sensor", label: "Sensore" },
  { value: "algo", label: "Algoritmo" },
];

export default function AdminInfoPoints() {
  const { api } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [selKey, setSelKey] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => {
    const qp = new URLSearchParams();
    if (category !== "all") qp.set("category", category);
    if (q) qp.set("q", q);
    const qs = qp.toString();
    api(`/infopoints${qs ? `?${qs}` : ""}`)
      .then((d) => setItems(d.items || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
     
  }, [category, q]);

  useEffect(() => {
    if (selKey) {
      const found = items.find((i) => i.key === selKey);
      if (found) setForm({ ...found });
    }
  }, [selKey, items]);

  const save = async () => {
    if (!form) return;
    setSaving(true);
    setMsg("");
    try {
      const res = await api(`/infopoints/${form.key}`, {
        method: "PUT",
        data: {
          category: form.category,
          title: form.title,
          short: form.short,
          how: form.how,
          source_type: form.source_type,
          source: form.source,
        },
      });
      setForm({ ...res });
      load();
      setMsg("Salvato. Attivo immediatamente nell'app utente.");
    } catch (e) {
      setMsg(`Errore: ${e.message || "salvataggio fallito"}`);
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 3500);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => {
      g[i.category] = g[i.category] || [];
      g[i.category].push(i);
    });
    return g;
  }, [items]);

  return (
    <div className="p-8" data-testid="admin-infopoints-page">
      <PageHeader
        title="Infopoint"
        subtitle="Le mini-card informative che spiegano metriche e sorgenti dati agli utenti dell'app"
      />

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-1 min-w-[240px] rounded-xl bg-white/5 border border-white/10 px-3 h-10">
          <Search size={15} className="text-white/45" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per chiave, titolo o descrizione…"
            className="flex-1 bg-transparent text-white text-[13px] focus:outline-none"
            data-testid="ip-search"
          />
        </div>
        <div className="flex items-center gap-1 rounded-xl bg-white/5 border border-white/10 p-1">
          {CATEGORIES.map((c) => (
            <button
              key={c.value}
              onClick={() => setCategory(c.value)}
              className={`h-8 px-3 rounded-lg text-[11.5px] font-medium transition-all ${
                category === c.value
                  ? "bg-[#14c2a1]/15 text-[#67e8d1]"
                  : "text-white/55 hover:text-white"
              }`}
              data-testid={`ip-cat-${c.value}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Lista */}
        <div className="col-span-12 lg:col-span-5">
          <Panel
            title={`${items.length} infopoint`}
            right={
              loading ? (
                <span className="text-white/50 text-[11px]">Caricamento…</span>
              ) : null
            }
          >
            <div className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
              {Object.entries(grouped).map(([cat, list]) => (
                <div key={cat}>
                  <div className="px-4 py-2 bg-white/[0.02] text-white/50 text-[10.5px] uppercase tracking-wider sticky top-0">
                    {CATEGORIES.find((c) => c.value === cat)?.label || cat}
                  </div>
                  {list.map((i) => (
                    <button
                      key={i.key}
                      onClick={() => setSelKey(i.key)}
                      className={`w-full px-4 py-3 text-left transition-colors ${
                        selKey === i.key
                          ? "bg-[#14c2a1]/10"
                          : "hover:bg-white/[0.03]"
                      }`}
                      data-testid={`ip-row-${i.key}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-white text-[13.5px] font-medium">
                          {i.title}
                        </span>
                        <Badge color="slate">{i.source_type}</Badge>
                      </div>
                      <div className="text-white/50 text-[11.5px] mt-0.5 font-mono">
                        {i.key}
                      </div>
                    </button>
                  ))}
                </div>
              ))}
              {items.length === 0 && !loading && (
                <div className="p-6 text-white/45 text-sm text-center">
                  Nessun infopoint con questi filtri.
                </div>
              )}
            </div>
          </Panel>
        </div>

        {/* Editor */}
        <div className="col-span-12 lg:col-span-7">
          {form ? (
            <Panel
              title={form.title}
              right={
                <div className="flex items-center gap-2">
                  {msg && (
                    <span
                      className={`text-[11.5px] ${
                        msg.startsWith("Errore")
                          ? "text-rose-300"
                          : "text-[#67e8d1]"
                      }`}
                    >
                      {msg}
                    </span>
                  )}
                  <Button
                    onClick={save}
                    disabled={saving}
                    data-testid="ip-save"
                  >
                    <Save size={13} className="mr-1" />
                    {saving ? "Salvataggio…" : "Salva"}
                  </Button>
                </div>
              }
            >
              <div className="p-4 space-y-3.5">
                <F label="Chiave (immutabile)">
                  <input
                    value={form.key}
                    disabled
                    className="w-full h-9 px-3 rounded-lg bg-white/[0.02] border border-white/10 text-white/60 text-[12.5px] font-mono"
                  />
                </F>
                <F label="Titolo">
                  <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="ip-field-title"
                    className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
                  />
                </F>
                <F label="Breve descrizione (1-2 righe)">
                  <textarea
                    rows={2}
                    value={form.short || ""}
                    onChange={(e) => setForm({ ...form, short: e.target.value })}
                    data-testid="ip-field-short"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1] resize-y"
                  />
                </F>
                <F label="Come viene calcolato (2-3 righe, senza dire troppo)">
                  <textarea
                    rows={3}
                    value={form.how || ""}
                    onChange={(e) => setForm({ ...form, how: e.target.value })}
                    data-testid="ip-field-how"
                    className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1] resize-y"
                  />
                </F>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Tipo sorgente">
                    <select
                      value={form.source_type || ""}
                      onChange={(e) =>
                        setForm({ ...form, source_type: e.target.value })
                      }
                      className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
                    >
                      {["sensor", "mic", "diary", "algorithm"].map((o) => (
                        <option key={o} value={o} className="bg-[#0e1517]">
                          {o}
                        </option>
                      ))}
                    </select>
                  </F>
                  <F label="Categoria">
                    <select
                      value={form.category || ""}
                      onChange={(e) =>
                        setForm({ ...form, category: e.target.value })
                      }
                      className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
                    >
                      {CATEGORIES.filter((c) => c.value !== "all").map((c) => (
                        <option
                          key={c.value}
                          value={c.value}
                          className="bg-[#0e1517]"
                        >
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </F>
                </div>
                <F label="Sorgente (leggibile per l'utente)">
                  <input
                    value={form.source || ""}
                    onChange={(e) => setForm({ ...form, source: e.target.value })}
                    data-testid="ip-field-source"
                    className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-[13px] focus:outline-none focus:border-[#14c2a1]"
                  />
                </F>
                {form.updated_at && (
                  <div className="text-white/40 text-[10.5px] pt-2 border-t border-white/5">
                    Ultima modifica: {new Date(form.updated_at).toLocaleString("it-IT")}{" "}
                    — {form.updated_by || "sistema"}
                  </div>
                )}
              </div>
            </Panel>
          ) : (
            <Panel title="Seleziona un infopoint">
              <div className="p-10 text-center text-white/45 text-sm flex flex-col items-center gap-3">
                <Info size={28} className="text-white/30" />
                Seleziona un infopoint dalla lista per modificarne titolo,
                descrizione, sorgente e spiegazione del calcolo.
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="text-white/60 text-[10.5px] uppercase tracking-wider mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}
